import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import TierBadge from "../components/TierBadge";
import { toast } from "sonner";
import { Users, ShieldCheck, ShieldOff, Trash2, Mail, Clock, Search, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";

function providerBadge(p) {
  if (p === "google") return <span className="text-[10px] uppercase tracking-[0.15em] text-sky-300 bg-sky-500/10 border border-sky-500/20 rounded px-1.5 py-0.5">Google</span>;
  return <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 bg-white/5 border border-white/10 rounded px-1.5 py-0.5">Mot de passe</span>;
}

// Bouton-pastille générique (actif = vert)
function Pill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 h-9 rounded-md border text-sm transition-colors ${
        active
          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
          : "bg-white/[0.03] border-white/10 text-zinc-400 hover:text-zinc-200 hover:border-white/20"
      }`}
    >
      {children}
    </button>
  );
}

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();

  // Lecture des filtres depuis l'URL (persistance au rafraîchissement / partage de lien)
  const q = sp.get("q") || "";
  const role = sp.get("role") || "all";
  const plan = sp.get("plan") || "all";
  const since = sp.get("since") || "0";
  const twofa = sp.get("twofa") === "1";
  const hideTest = sp.get("hide_test") === "1";
  const sort = sp.get("sort") || "recent";
  const page = Math.max(1, parseInt(sp.get("page") || "1", 10));

  // Met à jour un paramètre d'URL (remet la page à 1 sauf changement de page)
  const setParam = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value === "" || value === null || value === undefined || value === "all" || value === "0" || value === false) {
      next.delete(key);
    } else {
      next.set(key, value === true ? "1" : String(value));
    }
    if (key !== "page") next.delete("page");
    setSp(next, { replace: true });
  };

  const reset = () => setSp(new URLSearchParams(), { replace: true });

  // Recherche avec délai de 300 ms
  const [search, setSearch] = useState(q);
  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== q) setParam("q", search.trim());
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const params = { q, role, plan, since_days: Number(since), twofa, hide_test: hideTest, sort, page, limit: 25 };
  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", params],
    queryFn: async () => (await api.get("/admin/users", { params })).data,
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalAll = data?.total_all ?? 0;
  const pages = data?.pages || 1;

  const setRole = useMutation({
    mutationFn: async ({ user_id, role }) => (await api.patch(`/admin/users/${user_id}/role`, { role })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); toast.success("Rôle mis à jour"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const del = useMutation({
    mutationFn: async (user_id) => (await api.delete(`/admin/users/${user_id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); toast.success("Utilisateur supprimé"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const hasFilters = q || role !== "all" || plan !== "all" || since !== "0" || twofa || hideTest || sort !== "recent";

  return (
    <div className="min-h-screen">
      <section className="border-b border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">Console admin</div>
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-md bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
              <Users className="w-5 h-5 text-emerald-400" />
            </span>
            <h1 className="font-display font-black text-3xl sm:text-4xl">Gestion des utilisateurs</h1>
          </div>
          <p className="mt-3 text-zinc-400">
            Voir les inscriptions, promouvoir ou rétrograder des comptes, supprimer un accès.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10">
        {/* Barre de filtres */}
        <div className="mb-5 space-y-3" data-testid="admin-users-filters">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, email ou pseudo…"
                className="w-full rounded-md bg-white/[0.03] border border-white/10 pl-9 pr-3 h-9 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500/50"
                data-testid="users-search"
              />
            </div>

            <Select value={role} onValueChange={(v) => setParam("role", v)}>
              <SelectTrigger className="w-36 bg-white/[0.03] border-white/10" data-testid="users-role-filter">
                <SelectValue placeholder="Rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">Client</SelectItem>
              </SelectContent>
            </Select>

            <Select value={plan} onValueChange={(v) => setParam("plan", v)}>
              <SelectTrigger className="w-36 bg-white/[0.03] border-white/10" data-testid="users-plan-filter">
                <SelectValue placeholder="Offre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les offres</SelectItem>
                <SelectItem value="free">Gratuit</SelectItem>
                <SelectItem value="essentiel">Essentiel</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-40 bg-white/[0.03] border-white/10" data-testid="users-sort">
                <SelectValue placeholder="Tri" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Plus récents</SelectItem>
                <SelectItem value="name">Nom A→Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-[0.15em] text-zinc-500 mr-1">Inscrits</span>
            <Pill active={since === "0"} onClick={() => setParam("since", "0")} testid="users-since-all">Tous</Pill>
            <Pill active={since === "7"} onClick={() => setParam("since", "7")} testid="users-since-7">7 jours</Pill>
            <Pill active={since === "30"} onClick={() => setParam("since", "30")} testid="users-since-30">30 jours</Pill>
            <span className="w-px h-6 bg-white/10 mx-1" />
            <Pill active={twofa} onClick={() => setParam("twofa", !twofa)} testid="users-twofa">2FA activée</Pill>
            <Pill active={hideTest} onClick={() => setParam("hide_test", !hideTest)} testid="users-hide-test">Masquer comptes test</Pill>

            <div className="ml-auto flex items-center gap-3">
              <span className="text-sm text-zinc-400" data-testid="users-counter">
                <b className="text-zinc-200">{total}</b> utilisateur{total > 1 ? "s" : ""} sur {totalAll}
              </span>
              {hasFilters && (
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"
                  data-testid="users-reset"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
                </button>
              )}
            </div>
          </div>
        </div>

        <Card className="bg-card border-white/5 overflow-hidden" data-testid="admin-users-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500 bg-white/[0.02]">
                  <th className="px-4 py-3">Utilisateur</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Offre</th>
                  <th className="px-4 py-3">2FA</th>
                  <th className="px-4 py-3">Inscrit</th>
                  <th className="px-4 py-3">Rôle</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={7} className="py-20 text-center text-zinc-500">Chargement…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="py-20 text-center text-zinc-500" data-testid="users-empty">Aucun utilisateur ne correspond à ces filtres</td></tr>
                ) : (
                  items.map((u) => {
                    const isMe = me && (me.id === u.user_id || me.email === u.email);
                    const created = u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—";
                    return (
                      <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-white/5 grid place-items-center text-xs font-mono-num">
                                {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="font-medium">
                                {u.name || "—"}
                                {u.role === "admin" && u.pseudo && (
                                  <span className="text-emerald-400 text-xs ml-1.5" data-testid={`user-pseudo-${u.user_id}`}>@{u.pseudo}</span>
                                )}
                                {isMe && <span className="text-[10px] text-emerald-400 ml-1">(vous)</span>}
                              </div>
                              <div className="text-xs text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">{providerBadge(u.provider)}</td>
                        <td className="px-4 py-3"><TierBadge tier={u.plan || "free"} /></td>
                        <td className="px-4 py-3">
                          {u.totp_enabled ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><ShieldCheck className="w-3.5 h-3.5" /> Activée</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-zinc-500"><ShieldOff className="w-3.5 h-3.5" /> Désactivée</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-400 font-mono-num"><Clock className="w-3 h-3 inline mr-1" />{created}</td>
                        <td className="px-4 py-3">
                          <Select
                            value={u.role}
                            onValueChange={(role) => setRole.mutate({ user_id: u.user_id, role })}
                            disabled={isMe}
                          >
                            <SelectTrigger className="w-32 bg-white/[0.03] border-white/10" data-testid={`role-select-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Client</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                className="p-1.5 rounded hover:bg-rose-500/10 text-rose-400 disabled:opacity-30 disabled:hover:bg-transparent"
                                disabled={isMe}
                                data-testid={`user-delete-${u.user_id}`}
                                aria-label="Supprimer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-card border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer {u.email} ?</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  Ceci supprime le compte, ses sessions et l'intégralité de son portefeuille. Irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del.mutate(u.user_id)} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid={`user-delete-confirm-${u.user_id}`}>
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/5 text-sm" data-testid="users-pagination">
              <span className="text-zinc-500">Page {page} sur {pages}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setParam("page", page - 1)}
                  disabled={page <= 1}
                  className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-white/10 text-zinc-300 hover:border-white/25 disabled:opacity-30"
                  data-testid="users-prev"
                >
                  <ChevronLeft className="w-4 h-4" /> Précédent
                </button>
                <button
                  type="button"
                  onClick={() => setParam("page", page + 1)}
                  disabled={page >= pages}
                  className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-white/10 text-zinc-300 hover:border-white/25 disabled:opacity-30"
                  data-testid="users-next"
                >
                  Suivant <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
